import { useState, useRef, useEffect } from 'react';
import { validateLogin } from '../providers/UserSessionProvider';
import './LoginPage.css';

interface LoginPageProps {
  onLoginSuccess: (email: string) => void;
}

interface FieldErrors {
  email?: string;
  password?: string;
}

export const LoginPage = ({ onLoginSuccess }: LoginPageProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const isFormValid = email.trim().length > 0 && password.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    const result = validateLogin(email, password);

    if (!result.valid) {
      const fieldErrors: FieldErrors = {};
      for (const err of result.errors) {
        // ponytail: first error per field wins — validation order guarantees correctness
        if (!fieldErrors[err.field]) {
          fieldErrors[err.field] = err.message;
        }
      }
      setErrors(fieldErrors);

      // Fire-and-forget tracking
      fetch('/api/tracking/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{
          email: email.trim().toLowerCase(),
          eventType: 'login_failed',
          timestamp: new Date().toISOString(),
          payload: {
            userAgent: navigator.userAgent.slice(0, 512),
            reason: result.errors.map(e => e.message).join('; '),
          },
        }]),
      }).catch(() => { /* ponytail: fire-and-forget */ });

      return;
    }

    setErrors({});
    onLoginSuccess(email.trim());
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <div className="login-wordmark">
          <span className="login-prefix">DTN</span>
          <h1 className="login-title">Station Explorer</h1>
        </div>

        <div className="login-field">
          <label htmlFor="login-email">Email</label>
          <input
            ref={emailRef}
            id="login-email"
            type="email"
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@dtn.com"
            autoComplete="email"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
          />
          {errors.email && (
            <p id="login-email-error" className="login-error" role="alert">{errors.email}</p>
          )}
        </div>

        <div className="login-field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
          />
          {errors.password && (
            <p id="login-password-error" className="login-error" role="alert">{errors.password}</p>
          )}
        </div>

        <button
          type="submit"
          className="login-submit"
          disabled={!isFormValid}
        >
          Sign In
        </button>
      </form>
    </div>
  );
};
